/**
 * Where the MCP sign-in page (/login?mode=oauth) may POST the user's Firebase ID token: the /authorize/callback of a
 * 4K Labs authorization server. The value arrives in the page's own query string, so anything else gets nothing.
 *
 * The URL parser decides, not a pattern: a lookalike host or a userinfo trick resolves to its real origin first.
 */
import { getApiBaseUrl } from '../../utils/apiConfig';

const AUTHORIZATION_SERVERS = ['https://api-aws.4klabs.ai', 'https://api-beta.4klabs.ai'];
const CALLBACK_PATH = '/authorize/callback';
const DEV_HOSTS = ['localhost', '127.0.0.1'];

function originOf(raw: string): string | null {
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

export function allowedOAuthCallback(raw: string, opts: { configuredApi: string; dev: boolean }): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.origin === 'null' || url.username || url.password || url.pathname !== CALLBACK_PATH) return false;
  if (AUTHORIZATION_SERVERS.includes(url.origin) || url.origin === originOf(opts.configuredApi)) return true;
  return opts.dev && url.protocol === 'http:' && DEV_HOSTS.includes(url.hostname);
}

export function isAllowedOAuthCallback(raw: string): boolean {
  return allowedOAuthCallback(raw, { configuredApi: getApiBaseUrl(), dev: import.meta.env.DEV });
}

/** Where the server says a code for this client and redirect would go, read from its own client record. */
export interface Destination {
  clientName: string | null;
  redirectHost: string;
  known: boolean;
}

export type DestinationAnswer = { refused: false; destination: Destination } | { refused: true; message: string };

function isDestination(v: unknown): v is Destination {
  const d = v as Destination | null;
  return !!d && typeof d.redirectHost === 'string' && typeof d.known === 'boolean' && (d.clientName === null || typeof d.clientName === 'string');
}

function hostOf(raw: string): string {
  try {
    return new URL(raw).host || raw;
  } catch {
    return raw;
  }
}

/**
 * Ask the link's authorization server (already checked to be ours) about this client and redirect. A 400 is the server
 * refusing the link; any other failure is answered as an unknown host named from the link, so it still asks the user.
 */
export async function lookupDestination(oauthCallback: string, clientId: string, redirectUri: string): Promise<DestinationAnswer> {
  const url = new URL('/authorize/client', oauthCallback);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', redirectUri);
  try {
    const res = await fetch(url.toString());
    const body = await res.json().catch(() => null);
    if (res.status === 400) return { refused: true, message: body?.message ?? body?.error ?? 'invalid request' };
    if (res.ok && isDestination(body)) return { refused: false, destination: body };
  } catch {
    // An unreachable server is answered below like any other failed lookup.
  }
  return { refused: false, destination: { clientName: null, redirectHost: hostOf(redirectUri), known: false } };
}
